/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useEffect, useState } from 'react';
import { Check, Library, Play, Search, Volume2 } from 'lucide-react';
import { api, playBlob, type ConfigStatus, type VoiceEffect, type VoiceSettings, type SharedVoice } from '../lib/api';
import { errorText } from '../lib/errors';
import { Button, Callout, Dialog, EmptyState, Field, Grid, Input, MeterRow, PageHeader, Panel, Select, Skeleton, Stack, StatusChip } from '../ui';
import { count, percent, ratio } from '../ui/format';

const SLIDERS: Array<{ key: keyof VoiceSettings; label: string; min: number; max: number; step: number; hint: string }> = [
  { key: 'speed', label: 'Speed', min: 0.7, max: 1.2, step: 0.01, hint: 'slower ↔ faster' },
  { key: 'stability', label: 'Stability', min: 0, max: 1, step: 0.05, hint: 'expressive ↔ steady' },
  { key: 'similarity', label: 'Similarity', min: 0, max: 1, step: 0.05, hint: 'closeness to source' },
  { key: 'style', label: 'Style', min: 0, max: 1, step: 0.05, hint: 'neutral ↔ dramatic' },
];

/** ElevenLabs models the console offers by name; any other id the service already holds is kept as "(current)". */
const MODELS: Array<{ id: string; label: string }> = [
  { id: 'eleven_v4', label: 'Eleven v4 (newest; stability and similarity only)' },
  { id: 'eleven_v3', label: 'Eleven v3 (stability and similarity only)' },
  { id: 'eleven_multilingual_v2', label: 'Multilingual v2' },
  { id: 'eleven_turbo_v2_5', label: 'Turbo v2.5 (fastest)' },
  { id: 'eleven_flash_v2_5', label: 'Flash v2.5' },
];

const EFFECTS: Array<{ id: VoiceEffect; label: string; hint: string }> = [
  { id: 'none', label: 'None', hint: 'The voice as ElevenLabs renders it.' },
  { id: 'light-robot', label: 'Light robot', hint: 'A quiet machine shimmer under the same voice: a band-limited copy shifted up 70 Hz, mixed in at 10 %. Pitch and pace unchanged. The owner approved this exact recipe on 2026-10-13.' },
];

/**
 * Settings › Voice, rebuilt on the kit (overhaul step 4): one voice for every app. Set the ElevenLabs key, pick a voice
 * (or browse the library), tune it, and preview; all apps inherit it. The same calls and saves as before.
 */
export function Voice() {
  const [cfg, setCfg] = useState<ConfigStatus | null>(null);
  const [elevenKey, setElevenKey] = useState('');
  const [voiceId, setVoiceId] = useState('');
  const [vset, setVset] = useState<VoiceSettings>({ speed: 1, stability: 0.45, similarity: 0.8, style: 0.15 });
  const [model, setModel] = useState('');
  const [effect, setEffect] = useState<VoiceEffect>('none');
  const [voices, setVoices] = useState<Array<{ voiceId: string; name: string; category?: string }>>([]);
  const [usage, setUsage] = useState<{ configured: boolean; usage: { tier: string | null; characterCount: number; characterLimit: number; remaining: number } | null } | null>(null);
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [showLib, setShowLib] = useState(false);

  const load = () =>
    api.getConfig().then((c) => {
      setCfg(c);
      setVoiceId(c.voiceId);
      setVset(c.voiceSettings);
      setModel(c.voiceModel ?? '');
      setEffect(c.voiceEffect ?? 'none');
      if (c.hasElevenKey) {
        api.voices().then((v) => setVoices(v.voices)).catch(() => undefined);
        api.voiceUsage().then(setUsage).catch(() => undefined);
      }
    });

  useEffect(() => {
    void load();
  }, []);

  const save = async (patch: Record<string, unknown>) => {
    setSaving(true);
    setMsg(null);
    try {
      const c = await api.saveConfig(patch);
      setCfg(c);
      setVoiceId(c.voiceId);
      setVset(c.voiceSettings);
      setModel(c.voiceModel ?? '');
      setEffect(c.voiceEffect ?? 'none');
      setElevenKey('');
      setMsg('Saved.');
      if (c.hasElevenKey) api.voices().then((v) => setVoices(v.voices)).catch(() => undefined);
    } catch (e) {
      // The service names the field it refused (voiceSettings, voiceId, elevenLabsKey); nothing was stored.
      setMsg(`Not saved: ${errorText(e, 'the service refused the change.')}`);
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    setPreviewing(true);
    setMsg(null);
    try {
      // The unsaved model and effect go with the preview, so what plays is what Save would make everyone hear.
      const blob = await api.ttsPreview({ voiceId, settings: vset, model: model || undefined, effect });
      if (blob) playBlob(blob);
      else setMsg('Preview unavailable — check the ElevenLabs key.');
    } finally {
      setPreviewing(false);
    }
  };

  const used = usage?.usage ? ratio(usage.usage.characterCount, usage.usage.characterLimit) : null;

  return (
    <>
      <PageHeader
        meta={cfg ? (cfg.hasElevenKey ? <StatusChip tone="ok">ElevenLabs voice on</StatusChip> : <StatusChip tone="neutral">Browser voice fallback</StatusChip>) : <Skeleton width="160px" />}
      />
      <div className="ui-page">
        <Stack>
          {msg && <Callout tone={msg.startsWith('Not saved') || msg.startsWith('Preview unavailable') ? 'bad' : 'ok'} role={msg.startsWith('Not saved') ? 'alert' : 'status'}>{msg}</Callout>}
          {!cfg ? (
            <Grid cols={2}>
              <Panel title="Voice & key" description="The ElevenLabs key and the voice every app speaks with"><div className="ui-form" aria-hidden="true"><Skeleton size="lg" /><Skeleton size="lg" /></div></Panel>
              <Panel title="Tuning" description="How the voice sounds"><div className="ui-form" aria-hidden="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="lg" /></div></Panel>
            </Grid>
          ) : (
            <Grid cols={2}>
              <Panel
                title={<span className="ui-title-icon"><Volume2 size={16} strokeWidth={1.75} aria-hidden="true" />Voice &amp; key</span>}
                description="The ElevenLabs key and the voice every app speaks with"
              >
                <div className="ui-form">
                  <Field label={<>ElevenLabs API key {cfg.hasElevenKey && <span className="ui-quiet">(leave blank to keep)</span>}</>}>
                    <Input type="password" placeholder="sk_…" value={elevenKey} autoComplete="off" onChange={(e) => setElevenKey(e.target.value)} />
                  </Field>
                  <Field label="Voice">
                    {voices.length > 0 ? (
                      <Select value={voiceId} onChange={(e) => setVoiceId(e.target.value)}>
                        <option value="">Default (George — calm British male)</option>
                        {voices.map((v) => (
                          <option key={v.voiceId} value={v.voiceId}>{v.name}{v.category ? ` · ${v.category}` : ''}</option>
                        ))}
                        {voiceId && !voices.some((v) => v.voiceId === voiceId) && <option value={voiceId}>{voiceId} (current)</option>}
                      </Select>
                    ) : (
                      <Input placeholder="Voice ID (optional)" value={voiceId} onChange={(e) => setVoiceId(e.target.value)} />
                    )}
                  </Field>
                  <div className="ui-actions">
                    <Button variant="primary" icon={Check} busy={saving} onClick={() => void save(elevenKey ? { elevenLabsKey: elevenKey, voiceId } : { voiceId })}>Save</Button>
                    <Button icon={Library} disabled={!cfg.hasElevenKey && !elevenKey} onClick={() => setShowLib(true)}>Browse library</Button>
                    <Button icon={Play} busy={previewing} disabled={!cfg.hasElevenKey && !elevenKey} onClick={() => void preview()}>Preview</Button>
                  </div>
                </div>
              </Panel>

              <Panel title="Tuning" description="How the voice sounds; every app inherits it">
                <div className="ui-form">
                  <Field label="Model" help={/^eleven_v[3-9]/.test(model) ? 'This model takes Stability and Similarity only; Speed and Style do not apply.' : 'Which ElevenLabs model renders the voice.'}>
                    <Select aria-label="Model" value={model} onChange={(e) => setModel(e.target.value)}>
                      {MODELS.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
                      {model && !MODELS.some((m) => m.id === model) && <option value={model}>{model} (current)</option>}
                    </Select>
                  </Field>
                  <Field label="Effect" help={EFFECTS.find((e) => e.id === effect)?.hint}>
                    <Select aria-label="Effect" value={effect} onChange={(e) => setEffect(e.target.value as VoiceEffect)}>
                      {EFFECTS.map((e) => <option key={e.id} value={e.id}>{e.label}</option>)}
                    </Select>
                  </Field>
                  {SLIDERS.map((s) => (
                    <Field key={s.key} label={<>{s.label} <span className="ui-quiet">{vset[s.key].toFixed(2)}</span></>} help={s.hint}>
                      <input className="ui-range" type="range" min={s.min} max={s.max} step={s.step} value={vset[s.key]} onChange={(e) => setVset((v) => ({ ...v, [s.key]: Number(e.target.value) }))} />
                    </Field>
                  ))}
                  <div className="ui-actions">
                    <Button variant="primary" icon={Check} busy={saving} onClick={() => void save({ voiceSettings: vset, voiceModel: model || null, voiceEffect: effect })}>Save tuning</Button>
                    <Button icon={Play} busy={previewing} disabled={!cfg.hasElevenKey && !elevenKey} onClick={() => void preview()}>Preview</Button>
                  </div>
                </div>
              </Panel>

              {usage?.configured && usage.usage && (
                <Panel title="ElevenLabs usage" description="Characters spoken this billing period">
                  <MeterRow
                    label={usage.usage.tier ?? 'tier'}
                    figure={<><strong>{count(usage.usage.characterCount)}</strong> <span className="ui-quiet">of {count(usage.usage.characterLimit)} characters</span></>}
                    value={used}
                    meterLabel={`ElevenLabs characters, ${used === null ? 'no limit' : `${percent(used)} used`}`}
                    tone={used !== null && used >= 1 ? 'bad' : used !== null && used >= 0.8 ? 'warn' : 'data'}
                    footStart={used === null ? null : `${percent(used)} used`}
                    footEnd={`${count(usage.usage.remaining)} characters remaining`}
                  />
                </Panel>
              )}
            </Grid>
          )}
        </Stack>
      </div>

      {showLib && <VoiceLibrary onClose={() => setShowLib(false)} onPicked={(id) => { setVoiceId(id); void load(); setShowLib(false); }} />}
    </>
  );
}

function VoiceLibrary({ onClose, onPicked }: { onClose: () => void; onPicked: (voiceId: string) => void }) {
  const [search, setSearch] = useState('');
  const [gender, setGender] = useState('');
  const [voices, setVoices] = useState<SharedVoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const run = (opts: { search?: string; gender?: string } = {}) => {
    setLoading(true);
    setNote(null);
    api
      .voiceLibrary({ search: opts.search ?? search, gender: opts.gender ?? gender })
      .then((r) => {
        setVoices(r.voices);
        setNote(r.error ?? r.note ?? null);
      })
      .catch((e) => setNote(e instanceof Error ? e.message : 'Failed'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    run({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog open onClose={onClose} title="ElevenLabs Voice Library" description="Search the shared voices and add one to your account" className="ui-dialog--wide">
      <form className="ui-actions" onSubmit={(e) => { e.preventDefault(); run({}); }}>
        <Input className="ui-grow" placeholder="Search (British, deep, narration…)" aria-label="Search voices" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select className="ui-w-sm" aria-label="Gender" value={gender} onChange={(e) => { setGender(e.target.value); run({ gender: e.target.value }); }}>
          <option value="">Any gender</option>
          <option value="male">Male</option>
          <option value="female">Female</option>
          <option value="neutral">Neutral</option>
        </Select>
        <Button variant="primary" type="submit" icon={Search} busy={loading}>Search</Button>
      </form>
      {note && <Callout tone="warn">{note}</Callout>}
      {loading && voices.length === 0 && <div className="ui-form" aria-hidden="true"><Skeleton size="lg" /><Skeleton size="lg" /><Skeleton size="lg" /></div>}
      {!loading && voices.length === 0 && <EmptyState compact icon={Library} title="No voices found">Try another word, or any gender.</EmptyState>}
      {voices.length > 0 && (
        <ul className="ui-voice-list">
          {voices.map((v) => (
            <li key={`${v.publicOwnerId}-${v.voiceId}`}>
              <span className="ui-cell-stack">
                <strong>{v.name}</strong>
                <span className="ui-cell-sub">{[v.gender, v.accent, v.age, v.useCase].filter(Boolean).join(' · ') || '—'}</span>
              </span>
              <span className="ui-row-actions">
                <Button variant="ghost" size="sm" icon={Play} disabled={!v.previewUrl} onClick={() => v.previewUrl && api.libraryPreview(v.previewUrl).then((b) => b && playBlob(b))} title="Preview" aria-label={`Preview ${v.name}`} />
                <Button variant="primary" size="sm" icon={Check} busy={busy === v.voiceId} onClick={async () => {
                  setBusy(v.voiceId);
                  try {
                    const r = await api.useLibraryVoice({ publicOwnerId: v.publicOwnerId, voiceId: v.voiceId, name: v.name });
                    if (r.voiceId) onPicked(r.voiceId);
                    else setNote(r.message ?? 'Could not add this voice.');
                  } finally {
                    setBusy(null);
                  }
                }}>Use</Button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  );
}
